/**
 * Admin Security Settings Page
 * 
 * Allows super_admins to:
 * - Toggle global MFA enforcement
 * - View notification audit logs
 * - Configure security settings
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Switch } from '../components/ui/switch';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { 
  Shield, 
  ShieldCheck, 
  ShieldAlert,
  Mail, 
  MessageSquare, 
  Bell,
  RefreshCw,
  Filter,
  Download,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Clock,
  Settings2
} from 'lucide-react';
import api from '../lib/api';

// Status badge component
const statusConfig = {
  pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
  sent: { icon: CheckCircle, color: 'bg-blue-100 text-blue-800', label: 'Sent' },
  delivered: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Delivered' },
  read: { icon: CheckCircle, color: 'bg-green-100 text-green-800', label: 'Read' },
  failed: { icon: XCircle, color: 'bg-red-100 text-red-800', label: 'Failed' },
  bounced: { icon: AlertTriangle, color: 'bg-orange-100 text-orange-800', label: 'Bounced' },
};

const StatusBadge = ({ status }) => {
  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;
  
  return (
    <Badge className={`${config.color} flex items-center gap-1`}>
      <Icon className="h-3 w-3" />
      {config.label}
    </Badge>
  );
};

// Channel icon component
const ChannelIcon = ({ channel }) => {
  const icons = {
    email: <Mail className="h-4 w-4" />,
    whatsapp: <MessageSquare className="h-4 w-4 text-green-600" />,
    sms: <MessageSquare className="h-4 w-4" />,
    push: <Bell className="h-4 w-4" />,
  };
  return icons[channel] || <Bell className="h-4 w-4" />;
};

const AdminSecuritySettings = () => {
  const [loading, setLoading] = useState(true);
  const [mfaEnforced, setMfaEnforced] = useState(false);
  const [toggling, setToggling] = useState(false);
  
  // Notification audit logs
  const [auditLogs, setAuditLogs] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);
  const [auditPage, setAuditPage] = useState(1);
  const [auditTotal, setAuditTotal] = useState(0);
  const [auditFilter, setAuditFilter] = useState({
    channel: 'all',
    status: 'all',
  });

  // Test notification
  const [testChannel, setTestChannel] = useState('email');
  const [testRecipient, setTestRecipient] = useState('');
  const [testMessage, setTestMessage] = useState('This is a test notification from LetWise.');
  const [sending, setSending] = useState(false);

  // Load security settings
  const loadSettings = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/settings/security');
      const settings = response.data.settings;
      setMfaEnforced(settings.mfa_enforced_globally || false);
    } catch (error) {
      console.error('Failed to load security settings:', error);
      // If endpoint doesn't exist yet, just ignore
    } finally {
      setLoading(false);
    }
  }, []);

  // Load audit logs
  const loadAuditLogs = useCallback(async () => {
    try {
      setAuditLoading(true);
      const params = new URLSearchParams({
        page: auditPage.toString(),
        limit: '20',
      });
      
      if (auditFilter.channel && auditFilter.channel !== 'all') params.set('channel', auditFilter.channel);
      if (auditFilter.status && auditFilter.status !== 'all') params.set('status', auditFilter.status);
      
      const response = await api.get(`/notifications/audit-logs?${params.toString()}`);
      setAuditLogs(response.data.logs || []);
      setAuditTotal(response.data.total || 0);
    } catch (error) {
      console.error('Failed to load audit logs:', error);
    } finally {
      setAuditLoading(false);
    }
  }, [auditPage, auditFilter]);

  useEffect(() => {
    loadSettings();
    loadAuditLogs();
  }, [loadSettings, loadAuditLogs]);

  // Toggle MFA enforcement
  const handleToggleMFA = async (enabled) => {
    try {
      setToggling(true);
      await api.put('/admin/settings/mfa-enforcement', { enabled });
      setMfaEnforced(enabled);
      toast.success(enabled ? 'MFA enforcement enabled' : 'MFA enforcement disabled');
    } catch (error) {
      console.error('Failed to toggle MFA:', error);
      toast.error('Failed to update MFA settings');
    } finally {
      setToggling(false);
    }
  };

  // Send test notification
  const handleSendTest = async () => {
    if (!testRecipient) {
      toast.error('Please enter a recipient');
      return;
    }
    
    try {
      setSending(true);
      const response = await api.post('/notifications/test', {
        channel: testChannel,
        recipient: testRecipient,
        message: testMessage,
      });
      
      if (response.data.success) {
        toast.success(`Test ${testChannel} notification sent!`);
        loadAuditLogs(); // Refresh logs
      } else {
        toast.error(response.data.error || 'Failed to send notification');
      }
    } catch (error) {
      console.error('Failed to send test notification:', error);
      toast.error('Failed to send test notification');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="container mx-auto p-6 space-y-6" data-testid="admin-security-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Shield className="h-6 w-6" />
            Security Settings
          </h1>
          <p className="text-muted-foreground">
            Manage MFA enforcement, notifications, and security policies
          </p>
        </div>
      </div>

      {/* MFA Enforcement Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {mfaEnforced ? (
              <ShieldCheck className="h-5 w-5 text-green-600" />
            ) : (
              <ShieldAlert className="h-5 w-5 text-yellow-600" />
            )}
            Multi-Factor Authentication
          </CardTitle>
          <CardDescription>
            Control whether MFA is required for all users
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
            <div>
              <p className="font-medium">Global MFA Enforcement</p>
              <p className="text-sm text-muted-foreground">
                {mfaEnforced 
                  ? 'All users are required to set up MFA'
                  : 'MFA is optional for users'}
              </p>
            </div>
            <Switch
              checked={mfaEnforced}
              onCheckedChange={handleToggleMFA}
              disabled={loading || toggling}
              data-testid="mfa-toggle"
            />
          </div>
          
          {mfaEnforced && (
            <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-sm text-green-800">
                <strong>MFA is enforced.</strong> Users will be required to verify their identity 
                using Email OTP, WhatsApp OTP, or an Authenticator app.
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Test Notification Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Settings2 className="h-5 w-5" />
            Test Notifications
          </CardTitle>
          <CardDescription>
            Send test notifications to verify email and WhatsApp are configured correctly
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div>
              <label className="text-sm font-medium mb-2 block">Channel</label>
              <Select value={testChannel} onValueChange={setTestChannel}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="email">Email</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="md:col-span-2">
              <label className="text-sm font-medium mb-2 block">
                {testChannel === 'email' ? 'Email Address' : 'Phone Number'}
              </label>
              <Input
                placeholder={testChannel === 'email' ? 'test@example.com' : '+447885507742'}
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
              />
            </div>
            
            <div className="flex items-end">
              <Button 
                onClick={handleSendTest} 
                disabled={sending}
                className="w-full"
                data-testid="send-test-notification"
              >
                {sending ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  'Send Test'
                )}
              </Button>
            </div>
          </div>
          
          <div className="mt-4">
            <label className="text-sm font-medium mb-2 block">Message</label>
            <Input
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
              placeholder="Test message content"
            />
          </div>
        </CardContent>
      </Card>

      {/* Notification Audit Logs */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Bell className="h-5 w-5" />
                Notification Audit Log
              </CardTitle>
              <CardDescription>
                Complete audit trail of all notifications sent
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={loadAuditLogs}
                disabled={auditLoading}
              >
                <RefreshCw className={`h-4 w-4 mr-2 ${auditLoading ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
              <Button variant="outline" size="sm">
                <Download className="h-4 w-4 mr-2" />
                Export
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex gap-4 mb-4">
            <Select 
              value={auditFilter.channel} 
              onValueChange={(v) => setAuditFilter(prev => ({ ...prev, channel: v }))}
            >
              <SelectTrigger className="w-40">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Channel" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Channels</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="whatsapp">WhatsApp</SelectItem>
                <SelectItem value="sms">SMS</SelectItem>
              </SelectContent>
            </Select>
            
            <Select 
              value={auditFilter.status} 
              onValueChange={(v) => setAuditFilter(prev => ({ ...prev, status: v }))}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                <SelectItem value="sent">Sent</SelectItem>
                <SelectItem value="delivered">Delivered</SelectItem>
                <SelectItem value="failed">Failed</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Logs Table */}
          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Channel</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Recipient</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Sent At</TableHead>
                  <TableHead>Provider</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {auditLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                      No notification logs found
                    </TableCell>
                  </TableRow>
                ) : (
                  auditLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <ChannelIcon channel={log.channel} />
                          <span className="capitalize">{log.channel}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{log.notification_type?.replace(/_/g, ' ')}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm font-mono">
                          {log.recipient_email || log.recipient_phone || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={log.status} />
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-muted-foreground">
                          {log.sent_at 
                            ? new Date(log.sent_at).toLocaleString() 
                            : new Date(log.created_at).toLocaleString()}
                        </span>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{log.provider || '-'}</span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>

          {/* Pagination */}
          {auditTotal > 20 && (
            <div className="flex items-center justify-between mt-4">
              <p className="text-sm text-muted-foreground">
                Showing {auditLogs.length} of {auditTotal} logs
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={auditPage === 1}
                  onClick={() => setAuditPage(p => p - 1)}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={auditLogs.length < 20}
                  onClick={() => setAuditPage(p => p + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminSecuritySettings;
